import { css, useTheme } from '@emotion/react';
import * as React from 'react';

import { ShippingLogo } from '~src/commons/components/logo/shipping-logo';
import { Colors } from '~src/theme/colors';
import { Spacing, spacing } from '~src/theme/spacing';

import * as Icons from '../../icons';
import * as LocationInfoIcons from '../../icons/location-info-icons';

export type IconType =
  | 'arrowDown'
  | 'arrowUp'
  | 'collectInStore'
  | 'closeIcon'
  | 'doorCode'
  | 'house'
  | 'info'
  | 'instructions'
  | 'location'
  | 'mailbox'
  | 'mapPinPointSelected'
  | 'mapPinPointUnselected'
  | 'customerNumber'
  | 'servicepoint'
  | 'carrier'
  | 'clear'
  | 'leaf'
  | 'lightning'
  | 'checkmark'
  | 'plussign'
  | 'questionmark'
  | 'atg'
  | 'pnl'
  | 'spelombud'
  | 'hja'
  | 'scan'
  | 'web'
  | 'catering'
  | 'shoppingBag'
  | 'phone'
  | 'shoppingCart'
  | 'coupon'
  | 'dbSchenker'
  | 'dhl'
  | 'accountBalance'
  | 'currencyExchange'
  | 'lottery'
  | 'minDoktor'
  | 'pharmacy'
  | 'selfscanning'
  | 'cross'
  | 'bakery'
  | 'delicacy'
  | 'homeDelivery'
  | 'postHandling'
  | 'sausages'
  | 'tickets'
  | 'counterDrugs'
  | 'riskGroups'
  | 'lunchbox'
  | 'wifi'
  | 'inlineLoader'
  | 'arrowTopLeft'
  | 'search'
  | 'caretRight';

export interface IconProps {
  color?: keyof Colors;
  className?: string;
  type: IconType;
  alignToText?: boolean;
  marginRight?: keyof Spacing;
  forwardedRef?: React.MutableRefObject<HTMLDivElement | null>;
  ariaName?: string;
  ariaHidden?: boolean | 'true' | 'false';
  width?: number;
  height?: number;
  onClick?: () => void;
}

export const Icon: React.FunctionComponent<IconProps> = ({
  type,
  color,
  className,
  alignToText,
  marginRight,
  forwardedRef,
  ariaHidden,
  ariaName,
  height,
  width,
  onClick,
}) => {
  const theme = useTheme();
  const fillColor = color ? theme[color] : theme.neutral80;
  return (
    <div
      aria-hidden={ariaHidden}
      css={styles.iconWrapperStyle({ alignToText, marginRight })}
      data-test="icon"
      className={className}
      ref={forwardedRef}
      onClick={onClick}
      onKeyPress={onClick}
      role="presentation"
      // TODO: Make this accessibile
    >
      {(() => {
        switch (type) {
          case 'arrowDown':
            return <Icons.ArrowDownIcon ariaName={ariaName} color={fillColor} />;
          case 'arrowUp':
            return <Icons.ArrowUpIcon ariaName={ariaName} color={fillColor} />;
          case 'collectInStore':
            return <Icons.CollectInStoreIcon ariaName={ariaName} color={fillColor} />;
          case 'closeIcon':
            return <Icons.CloseIcon ariaName={ariaName} color={fillColor} />;
          case 'doorCode':
            return <Icons.DoorcodeIcon ariaName={ariaName} color={fillColor} />;
          case 'house':
            return <Icons.HouseIcon ariaName={ariaName} color={fillColor} />;
          case 'info':
            return <Icons.InfoIcon ariaName={ariaName} color={fillColor} />;
          case 'instructions':
            return <Icons.InstructionsIcon ariaName={ariaName} color={fillColor} />;
          case 'location':
            return <Icons.LocationIcon ariaName={ariaName} color={fillColor} />;
          case 'mailbox':
            return <Icons.MailboxIcon ariaName={ariaName} color={fillColor} />;
          case 'mapPinPointSelected':
            return (
              <Icons.MapPinPointSelected
                ariaName={ariaName}
                color={color ? theme[color] : theme.black}
              />
            );
          case 'mapPinPointUnselected':
            return (
              <Icons.MapPinPointUnselected
                ariaName={ariaName}
                color={color ? theme[color] : theme.neutral40}
              />
            );
          case 'servicepoint':
            return <Icons.ServicePointIcon ariaName={ariaName} color={fillColor} />;
          case 'customerNumber':
            return <Icons.CustomerNumberIcon ariaName={ariaName} color={fillColor} />;
          case 'carrier':
            return <Icons.CarrierIcon ariaName={ariaName} color={fillColor} />;
          case 'clear':
            return (
              <Icons.ClearIcon
                ariaName={ariaName}
                width={width}
                height={height}
                color={fillColor}
              />
            );
          case 'leaf':
            return <Icons.Leaf ariaName={ariaName} />;
          case 'lightning':
            return <Icons.Lightning ariaName={ariaName} />;
          case 'checkmark':
            return <Icons.CheckMark ariaName={ariaName} />;
          case 'plussign':
            return <Icons.PlusSign ariaName={ariaName} color={fillColor} />;
          case 'questionmark':
            return <Icons.QuestionMark ariaName={ariaName} />;
          case 'atg':
            return <LocationInfoIcons.ATGServiceIcon />;
          case 'pnl':
            return <LocationInfoIcons.PostnordServiceIcon />;
          case 'spelombud':
            return <LocationInfoIcons.SpelombudServiceIcon />;
          case 'hja':
            return <LocationInfoIcons.Hjartat />;
          case 'scan':
            return <LocationInfoIcons.ScanIcon />;
          case 'web':
            return <LocationInfoIcons.WebsiteIcon />;
          case 'catering':
            return <LocationInfoIcons.CateringIcon />;
          case 'shoppingBag':
            return <LocationInfoIcons.ShoppingBagIcon />;
          case 'phone':
            return <LocationInfoIcons.ContactIcon />;
          case 'shoppingCart':
            return <LocationInfoIcons.ShoppingCartIcon />;
          case 'coupon':
            return <Icons.CouponIcon ariaName={ariaName} />;
          case 'dbSchenker':
            return <ShippingLogo shippingMethod={'dbs'} size={16} />;
          case 'dhl':
            return <ShippingLogo shippingMethod={'dhl'} size={16} />;
          case 'accountBalance':
            return <LocationInfoIcons.AccountBalance />;
          case 'currencyExchange':
            return <LocationInfoIcons.CurrencyExchange />;
          case 'lottery':
            return <LocationInfoIcons.Lottery />;
          case 'minDoktor':
            return <LocationInfoIcons.MinDoktor />;
          case 'pharmacy':
            return <LocationInfoIcons.Pharmacy />;
          case 'selfscanning':
            return <LocationInfoIcons.Selfscanning />;
          case 'bakery':
            return <LocationInfoIcons.BakeryIcon />;
          case 'delicacy':
            return <LocationInfoIcons.DelicacyIcon />;
          case 'homeDelivery':
            return <LocationInfoIcons.HomeDeliveryIcon />;
          case 'postHandling':
            return <LocationInfoIcons.PostHandlingIcon />;
          case 'sausages':
            return <LocationInfoIcons.SausagesIcon />;
          case 'tickets':
            return <LocationInfoIcons.TicketsIcon />;
          case 'counterDrugs':
            return <LocationInfoIcons.CounterDrugsIcon />;
          case 'riskGroups':
            return <LocationInfoIcons.RiskGroupsIcon />;
          case 'lunchbox':
            return <LocationInfoIcons.LunchboxIcon />;
          case 'wifi':
            return <LocationInfoIcons.WifiIcon />;
          case 'cross':
            return <Icons.CrossIcon color={fillColor} ariaName={ariaName} />;
          case 'inlineLoader':
            return <Icons.InlineLoader />;
          case 'arrowTopLeft':
            return <Icons.ArrowTopLeft />;
          case 'search':
            return <Icons.Search ariaName={ariaName} />;
          case 'caretRight':
            return <Icons.CaretRightIcon color={fillColor} />;
        }
      })()}
    </div>
  );
};

const styles = {
  iconWrapperStyle: ({
    alignToText,
    marginRight,
  }: Pick<IconProps, 'alignToText' | 'marginRight'>) =>
    css({
      // TODO: get rid of spacing object, it's used only in this place
      lineHeight: spacing.S4,
      height: spacing.S4,
      ...(marginRight && { marginRight: spacing[marginRight] }),
      ...(alignToText ? { position: 'relative', top: '-1px' } : {}),
    }),
};
